import React, { useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete required fields");
      return;
    }
    setBusy(true);
    try {
      await api.post("/messages", form);
      setSent(true);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid md:grid-cols-12 gap-10 items-start">
      <div className="md:col-span-5">
        <div className="overline"><span className="accent-line" /> Get in touch</div>
        <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
          Talk to the BizPulse <span className="italic text-accent">team</span>.
        </h2>
        <p className="text-muted-foreground leading-relaxed mt-5">
          Questions about plans, white-label, or custom Business Growth Discovery™ templates for your firm? Send us a note — a real human gets back within one business day.
        </p>
        <div className="mt-6 space-y-2 text-sm text-muted-foreground">
          <div>WhatsApp: <span className="text-foreground">+94 (0) 77 635 6704</span></div>
          <div>Hours: <span className="text-foreground">Sun–Thu · 09:00–18:00 (GMT+3)</span></div>
        </div>
      </div>

      <div className="md:col-span-6 md:col-start-7">
        <Card className="card-elevated p-7">
          {sent ? (
            <div className="text-center py-10" data-testid="contact-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
              <h3 className="font-display text-2xl mt-5">Message sent</h3>
              <p className="text-muted-foreground mt-2">We&apos;ll be in touch shortly.</p>
              <Button variant="outline" className="mt-6 rounded-full" onClick={() => setSent(false)} data-testid="contact-send-another">
                Send another
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name *</Label>
                  <Input data-testid="contact-name" className="mt-2" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email *</Label>
                  <Input data-testid="contact-email" type="email" className="mt-2" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Company</Label>
                <Input data-testid="contact-company" className="mt-2" value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Message *</Label>
                <Textarea data-testid="contact-message" rows={5} className="mt-2" value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} required
                  placeholder="Tell us a bit about your firm and what you're hoping to achieve…" />
              </div>
              <Button type="submit" data-testid="contact-submit" disabled={busy}
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-full">
                {busy ? "Sending…" : <>Send message <Send className="w-4 h-4 ml-2" /></>}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </section>
  );
}
